import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { 
  LogOut, Users, Mail, CheckCircle, XCircle, Clock, MapPin, TrendingUp, 
  Image, Shield, ChevronDown, ChevronUp, BarChart3, MessageSquare, 
  UserPlus, Search, Megaphone, AlertCircle, Activity, Star, Phone, Trash2, X, Edit, Key, Lock,
  Briefcase, Award, User, Play, Settings, Eye, EyeOff, RefreshCw, FileText, Plus
} from 'lucide-react';
import axios from 'axios';
import AdminMap from '../components/AdminMap';
import SearchDemandMap from '../components/SearchDemandMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Referral source labels for display
const REFERRAL_LABELS = {
  tv: 'TV Advert',
  radio: 'Radio',
  google: 'Google / Search',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  gas_safe_magazine: 'Gas Safe Magazine',
  trade_magazine: 'Trade Magazine',
  newspaper: 'Newspaper',
  billboard: 'Billboard / Poster',
  van_advert: 'Van Advert',
  word_of_mouth: 'Word of Mouth',
  colleague: 'Colleague / Friend',
  friend_family: 'Friend / Family',
  other: 'Other'
};

// Colors for the chart
const CHART_COLORS = [
  '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#E91E63', 
  '#00BCD4', '#8BC34A', '#FF5722', '#673AB7', '#3F51B5'
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [engineerTab, setEngineerTab] = useState('pending');
  const [engineerSearch, setEngineerSearch] = useState('');
  
  // Data states
  const [engineers, setEngineers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [overview, setOverview] = useState(null);
  const [fetchFailedCount, setFetchFailedCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [partnerEnquiries, setPartnerEnquiries] = useState([]);
  const [campaign, setCampaign] = useState({ subject: '', body: '', audience: 'all' });
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [expandedEngineers, setExpandedEngineers] = useState({});
  const [reviews, setReviews] = useState([]);
  const [reviewCounts, setReviewCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [reviewTab, setReviewTab] = useState('pending');
  const [imageViewer, setImageViewer] = useState({ open: false, src: '', title: '' });
  const [editModal, setEditModal] = useState({ open: false, engineer: null });
  const [editForm, setEditForm] = useState({ postcodes: '', businessPostcode: '', phone: '', email: '', businessName: '', yearsTrading: '', bio: '', accreditations: '', services: [] });
  const [saving, setSaving] = useState(false);
  const [resetPasswordModal, setResetPasswordModal] = useState({ open: false, engineer: null });
  const [resetPasswordForm, setResetPasswordForm] = useState({ adminPassword: '', newPassword: '' });
  const [showContacted, setShowContacted] = useState(false);
  const [trialModal, setTrialModal] = useState({ open: false, engineer: null });
  const [trialDays, setTrialDays] = useState(30);
  const [emailModal, setEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '', recipients: 'all' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('custom');
  const [searchAnalytics, setSearchAnalytics] = useState({ searches: [], totalSearches: 0, noResultTotal: 0 });
  const [resendingLogin, setResendingLogin] = useState(null);
  const [emailHistory, setEmailHistory] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [cannedEditor, setCannedEditor] = useState({ open: false, mode: 'create', id: null, name: '', subject: '', body: '' });
  const [savingCanned, setSavingCanned] = useState(false);
  const [seedingCanned, setSeedingCanned] = useState(false);
  const [replyModal, setReplyModal] = useState({ open: false, toEmail: '', toName: '', subject: '', message: '', context: '', cannedResponseId: '' });
  const [sendingReply, setSendingReply] = useState(false);

  const EMAIL_TEMPLATES = {
    custom: { label: 'Custom Message', subject: '', message: '' },
    welcome: {
      label: 'Welcome / Onboarding',
      subject: 'Welcome to The Gas Guide!',
      message: `Hi there,\n\nWelcome to The Gas Guide — the UK's trusted directory of Gas Safe registered engineers.\n\nYour profile is now live, and homeowners in your area can find you when searching for a reliable gas engineer.\n\nHere are a few tips to get the most out of your listing:\n\n1. Complete your profile — add a bio, work photos, and your accreditations\n2. Ask happy customers to leave a review on your profile\n3. Download your Gas Guide badges to use on your website, invoices, and social media\n\nIf you need any help, just reply to this email.\n\nBest regards,\nThe Gas Guide Team`
    },
    monthly_update: {
      label: 'Monthly Update',
      subject: 'The Gas Guide — Monthly Update',
      message: `Hi,\n\nHere's your monthly update from The Gas Guide.\n\n[Add your update content here]\n\nThanks for being part of The Gas Guide family.\n\nBest regards,\nThe Gas Guide Team`
    },
    special_offer: {
      label: 'Special Offer / Promotion',
      subject: 'Special Offer from The Gas Guide',
      message: `Hi,\n\nWe've got an exciting offer for you!\n\n[Describe your offer here]\n\nDon't miss out — this offer is available for a limited time only.\n\nBest regards,\nThe Gas Guide Team`
    },
    payment_reminder: {
      label: 'Payment / Subscription Reminder',
      subject: 'Your Gas Guide Subscription',
      message: `Hi,\n\nThis is a friendly reminder about your Gas Guide subscription.\n\nYour listing is a great way to reach new customers in your area. To keep your profile live and visible to homeowners, please ensure your subscription is up to date.\n\nLog in to your dashboard to manage your subscription: https://www.thegasguide.co.uk/engineer/login\n\nIf you have any questions, just reply to this email.\n\nBest regards,\nThe Gas Guide Team`
    },
    review_request: {
      label: 'Ask for Reviews',
      subject: 'Boost Your Gas Guide Profile with Reviews',
      message: `Hi,\n\nDid you know that engineers with reviews get significantly more enquiries?\n\nIf you've completed any jobs recently, ask your customers to leave a quick review on your Gas Guide profile. It only takes a minute and makes a big difference.\n\nCustomers can leave a review by visiting your public profile on thegasguide.co.uk.\n\nEngineers with 3+ reviews and a 4.7+ rating earn our "Top Rated" badge — a mark of excellence that builds trust with new customers.\n\nBest regards,\nThe Gas Guide Team`
    },
    trial_expired_catchup: {
      label: 'Trial Expired — Come Back',
      subject: 'We noticed your trial ended — here\'s how to stay listed',
      message: `Hi,\n\nWe noticed your free trial with The Gas Guide has recently ended, and your profile is no longer visible to homeowners searching for gas engineers in your area.\n\nDuring your trial, your listing was live and searchable — and we're still receiving postcode searches from people looking for engineers like you.\n\nThe good news is your profile, reviews, and everything you set up is still saved. You can get relisted instantly by subscribing.\n\nLog in here to reactivate: https://www.thegasguide.co.uk/engineer/login\n\nIf you have any questions about pricing or need any help, just reply to this email — we're happy to chat.\n\nBest regards,\nThe Gas Guide Team`
    }
  };

  const handleTemplateChange = (templateKey) => {
    setEmailTemplate(templateKey);
    const template = EMAIL_TEMPLATES[templateKey];
    if (templateKey !== 'custom') {
      setEmailForm(prev => ({ ...prev, subject: template.subject, message: template.message }));
    }
  };

  const toggleEngineerExpand = async (engineerId) => {
    const isOpening = !expandedEngineers[engineerId];
    setExpandedEngineers(prev => ({
      ...prev,
      [engineerId]: !prev[engineerId]
    }));

    // Lazy-fetch heavy documents on first expand
    if (isOpening) {
      const eng = engineers.find(e => e.id === engineerId);
      if (eng && !eng._documentsLoaded && (eng.hasInsuranceDoc || (eng.workImagesCount || 0) > 0)) {
        try {
          const token = localStorage.getItem('admin_token');
          const res = await axios.get(`${API}/admin/engineers/${engineerId}/documents`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 60000
          });
          setEngineers(prev => prev.map(e =>
            e.id === engineerId
              ? { ...e, insuranceDoc: res.data.insuranceDoc, workImages: res.data.workImages, _documentsLoaded: true }
              : e
          ));
        } catch (err) {
          console.error('Failed to fetch documents:', err);
          toast({ title: 'Failed to load documents', description: err.response?.data?.detail || err.message, variant: 'destructive' });
        }
      }
    }
  };

  useEffect(() => {
    checkAuth();
    fetchAllData();
    fetchCannedResponses();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        console.error('No admin token found');
        navigate('/admin/login');
        return;
      }
      const config = { 
        headers: { Authorization: `Bearer ${token}` },
        timeout: 45000
      };

      // Fire the 8 dashboard endpoints in parallel with generous timeouts
      const endpoints = [
        () => axios.get(`${API}/admin/engineers`, config),
        () => axios.get(`${API}/admin/contacts`, config),
        () => axios.get(`${API}/engineer-requests/stats`, { timeout: 45000 }),
        () => axios.get(`${API}/admin/analytics/referrals`, { timeout: 45000 }),
        () => axios.get(`${API}/admin/analytics/overview`, config),
        () => axios.get(`${API}/admin/reviews`, config),
        () => axios.get(`${API}/admin/search-analytics`, config),
        () => axios.get(`${API}/admin/partner-enquiries`, config)
      ];

      // Try once, then retry any that failed
      let results = await Promise.allSettled(endpoints.map(fn => fn()));
      const failedIdx = results
        .map((r, i) => r.status === 'rejected' ? i : -1)
        .filter(i => i >= 0);
      if (failedIdx.length > 0) {
        console.warn(`Retrying ${failedIdx.length} failed dashboard call(s):`, failedIdx);
        const retries = await Promise.allSettled(failedIdx.map(i => endpoints[i]()));
        retries.forEach((r, k) => {
          if (r.status === 'fulfilled') results[failedIdx[k]] = r;
        });
      }

      // Track which sections still failed after retry so we can show a banner
      const stillFailed = results
        .map((r, i) => r.status === 'rejected' ? i : -1)
        .filter(i => i >= 0);
      stillFailed.forEach(i => console.error(`Dashboard call ${i} failed after retry:`, results[i].reason?.message));
      setFetchFailedCount(stillFailed.length);

      const getData = (idx, fallback) => results[idx].status === 'fulfilled' ? results[idx].value.data : fallback;

      // Only overwrite state when the call actually succeeded — otherwise keep existing values
      if (results[0].status === 'fulfilled') setEngineers(getData(0, { engineers: [] }).engineers || []);
      if (results[1].status === 'fulfilled') setContacts(getData(1, { contacts: [] }).contacts || []);
      if (results[2].status === 'fulfilled') setHotspots(getData(2, { hotspots: [] }).hotspots || []);
      if (results[3].status === 'fulfilled') setReferrals(getData(3, { referrals: [] }).referrals || []);
      if (results[4].status === 'fulfilled') setOverview(getData(4, null));
      if (results[5].status === 'fulfilled') {
        const reviewData = getData(5, { reviews: [], counts: {} });
        setReviews(reviewData.reviews || []);
        setReviewCounts(reviewData.counts || { pending: 0, approved: 0, rejected: 0 });
      }
      if (results[6].status === 'fulfilled') {
        const searchData = getData(6, { searches: [], totalSearches: 0, noResultTotal: 0 });
        setSearchAnalytics(searchData);
      }
      if (results[7].status === 'fulfilled') {
        setPartnerEnquiries(getData(7, { enquiries: [] }).enquiries || []);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshEngineers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/engineers`, { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 });
      setEngineers(res.data.engineers || []);
    } catch (err) { console.error('Refresh engineers error:', err); }
  };

  const refreshContacts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 });
      setContacts(res.data.contacts || []);
    } catch (err) { console.error('Refresh contacts error:', err); }
  };

  const refreshReviews = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/reviews`, { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 });
      setReviews(res.data.reviews || []);
      setReviewCounts(res.data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (err) { console.error('Refresh reviews error:', err); }
  };

  const refreshHotspots = async () => {
    try {
      const res = await axios.get(`${API}/engineer-requests/stats`, { timeout: 10000 });
      setHotspots(res.data.hotspots || []);
    } catch (err) { console.error('Refresh hotspots error:', err); }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      toast({ title: 'Failed to load analytics', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchCannedResponses = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/canned-responses`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setCannedResponses(res.data.responses || []);
    } catch (err) {
      console.error('Canned responses fetch error:', err);
    }
  };

  const handleSaveCanned = async () => {
    const { mode, id, name, subject, body } = cannedEditor;
    if (!name.trim() || !subject.trim() || !body.trim()) {
      toast({ title: 'All fields required', description: 'Name, subject and body are all needed.', variant: 'destructive' });
      return;
    }
    setSavingCanned(true);
    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { name: name.trim(), subject: subject.trim(), body: body.trim() };
      if (mode === 'edit' && id) {
        await axios.put(`${API}/admin/canned-responses/${id}`, payload, config);
      } else {
        await axios.post(`${API}/admin/canned-responses`, payload, config);
      }
      setCannedEditor({ open: false, mode: 'create', id: null, name: '', subject: '', body: '' });
      await fetchCannedResponses();
      toast({ title: mode === 'edit' ? 'Response updated' : 'Response created' });
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSavingCanned(false);
    }
  };

  const handleDeleteCanned = async (id) => {
    if (!window.confirm('Delete this canned response? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/canned-responses/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      await fetchCannedResponses();
      toast({ title: 'Response deleted' });
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleSeedCanned = async () => {
    setSeedingCanned(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/canned-responses/seed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await fetchCannedResponses();
      toast({ title: 'Starters loaded', description: `Added ${res.data.inserted}, skipped ${res.data.skipped}.` });
    } catch (err) {
      toast({ title: 'Seed failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSeedingCanned(false);
    }
  };

  const openReplyModal = (toEmail, toName = '', context = '') => {
    if (cannedResponses.length === 0) fetchCannedResponses();
    setReplyModal({ open: true, toEmail, toName, subject: '', message: '', context, cannedResponseId: '' });
  };

  const applyCannedToReply = (responseId) => {
    const r = cannedResponses.find(x => x.id === responseId);
    if (!r) return;
    setReplyModal(prev => ({ ...prev, subject: r.subject, message: r.body, cannedResponseId: r.id }));
  };

  const applyCannedToCampaign = (responseId) => {
    const r = cannedResponses.find(x => x.id === responseId);
    if (!r) return;
    setCampaign(prev => ({ ...prev, subject: r.subject, body: r.body }));
  };

  const applyCannedToEmailModal = (responseId) => {
    const r = cannedResponses.find(x => x.id === responseId);
    if (!r) return;
    setEmailForm(prev => ({ ...prev, subject: r.subject, message: r.body }));
    setEmailTemplate('custom');
  };

  const handleSendReply = async () => {
    const { toEmail, toName, subject, message, cannedResponseId } = replyModal;
    if (!subject.trim() || !message.trim()) {
      toast({ title: 'Subject and message required', variant: 'destructive' });
      return;
    }
    setSendingReply(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/send-single-email`, {
        toEmail, toName, subject, message, cannedResponseId
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 });
      if (res.data.success) {
        toast({ title: 'Reply sent', description: `Emailed ${toEmail}` });
        setReplyModal({ open: false, toEmail: '', toName: '', subject: '', message: '', context: '', cannedResponseId: '' });
      } else {
        toast({ title: 'Send failed', description: res.data.message || 'SMTP error', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Send failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSendingReply(false);
    }
  };

  const handleApprove = async (engineerId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/engineers/${engineerId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Engineer Approved", description: "Email sent with payment instructions" });
      refreshEngineers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.error, variant: "destructive" });
    }
  };

  const handleReject = async (engineerId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/engineers/${engineerId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Application Rejected" });
      refreshEngineers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.error, variant: "destructive" });
    }
  };

  const handleDelete = async (engineerId, engineerName) => {
    if (!window.confirm(`Delete ${engineerName}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/engineers/${engineerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Engineer Deleted" });
      refreshEngineers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.error, variant: "destructive" });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const [sendingReminders, setSendingReminders] = useState(false);

  const handleSendLoginReminders = async () => {
    if (!window.confirm('Send reminder emails to all engineers who haven\'t logged in yet?')) return;
    setSendingReminders(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/send-login-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      window.alert(response.data.message || 'Reminders sent!');
    } catch (error) {
      window.alert('Error: ' + (error.response?.data?.detail || error.message || 'Failed to send reminders. Check your connection and try again.'));
    } finally {
      setSendingReminders(false);
    }
  };


  // Review management handlers
  const handleApproveReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/reviews/${reviewId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Review Approved", description: "Review is now published on engineer's profile" });
      refreshReviews();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to approve", variant: "destructive" });
    }
  };

  const handleRejectReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/reviews/${reviewId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Review Rejected", description: "Review has been rejected" });
      refreshReviews();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to reject", variant: "destructive" });
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Review Deleted" });
      refreshReviews();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to delete", variant: "destructive" });
    }
  };

  const handleMarkContacted = async (contactId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/contacts/${contactId}/status`, 
        { status: 'contacted' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Done", description: "Enquiry marked as contacted" });
      refreshContacts();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to update", variant: "destructive" });
    }
  };

  const handleDeleteAreaRequest = async (postcode) => {
    if (!window.confirm(`Remove all requests for ${postcode}?`)) return;
    try {
      await axios.delete(`${API}/engineer-requests/${postcode}`);
      toast({ title: "Removed", description: `Requests for ${postcode} removed` });
      refreshHotspots();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to remove", variant: "destructive" });
    }
  };

  const handleToggleDealtWith = async (postcode) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/search-dealt-with`, { postcode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchAnalytics(prev => ({
        ...prev,
        searches: prev.searches.map(s => s.postcode === postcode ? { ...s, dealtWith: res.data.dealtWith } : s)
      }));
      toast({ title: res.data.dealtWith ? "Dealt With" : "Unmarked", description: res.data.message });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  const handleResendLogin = async (engineer) => {
    if (!window.confirm(`Resend login details to ${engineer.fullName} (${engineer.email})?`)) return;
    setResendingLogin(engineer.id);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/admin/engineers/${engineer.id}/resend-login`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.alert(res.data.message || 'Login details resent!');
      // Refresh email history for this engineer
      fetchEmailHistory(engineer.id);
    } catch (error) {
      window.alert('Error: ' + (error.response?.data?.detail || 'Failed to resend'));
    } finally {
      setResendingLogin(null);
    }
  };

  const fetchEmailHistory = async (engineerId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/engineers/${engineerId}/email-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailHistory(prev => ({ ...prev, [engineerId]: res.data.emails || [] }));
    } catch (error) {
      // silent fail
    }
  };

  const handleSendReminder = async (engineerId, engineerName) => {
    if (!window.confirm(`Send reminder email to ${engineerName}?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/engineers/${engineerId}/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Reminder Sent", description: response.data.message });
      refreshEngineers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to send reminder", variant: "destructive" });
    }
  };

  const handleDeleteEngineer = async (engineerId, engineerName) => {
    const password = window.prompt(`To delete ${engineerName} permanently, enter your admin password:`);
    if (!password) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/engineers/${engineerId}/delete`, 
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Engineer Deleted", description: `${engineerName} has been removed` });
      refreshEngineers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to delete", variant: "destructive" });
    }
  };

  const openEditModal = (engineer) => {
    setEditForm({
      postcodes: engineer.postcodes?.join(', ') || '',
      businessPostcode: engineer.businessPostcode || '',
      phone: engineer.phone || '',
      email: engineer.email || '',
      businessName: engineer.businessName || '',
      yearsTrading: engineer.yearsTrading || '',
      bio: engineer.bio || '',
      accreditations: engineer.accreditations?.join(', ') || '',
      services: engineer.services || []
    });
    setEditModal({ open: true, engineer });
  };

  const handleSaveEdit = async () => {
    if (!editModal.engineer) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Parse postcodes from comma-separated string
      const postcodes = editForm.postcodes
        .split(/[,\s]+/)
        .map(pc => pc.trim().toUpperCase())
        .filter(pc => pc.length > 0);
      
      await axios.put(`${API}/admin/engineers/${editModal.engineer.id}`, {
        postcodes,
        businessPostcode: editForm.businessPostcode,
        phone: editForm.phone,
        email: editForm.email,
        businessName: editForm.businessName,
        yearsTrading: parseInt(editForm.yearsTrading) || 0,
        bio: editForm.bio,
        accreditations: editForm.accreditations.split(/[,]+/).map(a => a.trim()).filter(a => a),
        services: editForm.services
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Saved", description: "Engineer details updated" });
      setEditModal({ open: false, engineer: null });
      refreshEngineers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleAdminResetPassword = async () => {
    if (!resetPasswordModal.engineer) return;
    
    if (resetPasswordForm.newPassword.length < 8) {
      toast({ title: "Error", description: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/engineers/${resetPasswordModal.engineer.id}/admin-reset-password`, {
        password: resetPasswordForm.adminPassword,
        newEngineerPassword: resetPasswordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: `Password reset for ${resetPasswordModal.engineer.fullName}` });
      setResetPasswordModal({ open: false, engineer: null });
      setResetPasswordForm({ adminPassword: '', newPassword: '' });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to reset password", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleStartTrial = async () => {
    if (!trialModal.engineer) return;
    
    const password = window.prompt('Enter your admin password to confirm:');
    if (!password) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/engineers/${trialModal.engineer.id}/start-trial`, {
        days: trialDays,
        password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Trial Started", description: response.data.message });
      setTrialModal({ open: false, engineer: null });
      setTrialDays(30);
      refreshEngineers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to start trial", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const getTrialDaysRemaining = (engineer) => {
    if (!engineer.trialEndDate) return null;
    const end = new Date(engineer.trialEndDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const handleActivateTrialsForApproved = async () => {
    if (!window.confirm(`Activate 30-day free trials for all ${stats.approved} approved engineers and send them an email?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/activate-trials-for-approved`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Trials Activated", description: response.data.message });
      refreshEngineers();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to activate trials", variant: "destructive" });
    }
  };

  const handleBulkExtendExpired = async () => {
    const expiredCount = stats.trial_expired || engineers.filter(e => e.status === 'trial_expired').length;
    const days = window.prompt(`Extend all ${expiredCount} expired trials by how many days?`, '30');
    if (!days) return;
    const daysNum = parseInt(days);
    if (isNaN(daysNum) || daysNum < 1 || daysNum > 90) {
      window.alert('Please enter a number between 1 and 90');
      return;
    }
    const adminPassword = window.prompt('Enter admin password to confirm:');
    if (!adminPassword) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/bulk-extend-expired-trials`, 
        { days: daysNum, password: adminPassword },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      window.alert(response.data.message);
      refreshEngineers();
    } catch (error) {
      window.alert('Error: ' + (error.response?.data?.detail || 'Failed to extend trials'));
    }
  };

  // Migrate every engineer (active/approved/trial_expired) to a permanent FREE active listing.
  // Auth: admin JWT only (you're already logged in). One-click after confirmation.
  const handleMigrateAllToFree = async () => {
    if (!window.confirm('Convert ALL engineers (active, approved, trial-expired) to permanent FREE listings? Their trial dates will be cleared. This is safe to run multiple times.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/engineers/migrate-all-to-free`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      window.alert(response.data.message || 'Done.');
      refreshEngineers();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      let detail = data?.error || data?.detail;
      // FastAPI 422 returns detail as an array of {loc, msg, type} — format it
      if (Array.isArray(detail)) {
        detail = detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join('; ');
      } else if (detail && typeof detail === 'object') {
        detail = JSON.stringify(detail);
      }
      const rawText = typeof data === 'string' ? data.slice(0, 200) : '';
      const msg = detail
        ? `Error ${status}: ${detail}`
        : status
          ? `HTTP ${status} ${error.response?.statusText || ''}${rawText ? ' — ' + rawText : ''}`
          : `Network error: ${error.message || 'Could not reach the server.'}`;
      window.alert(msg);
    }
  };

  // Reset the freeAnnouncementSent flag so all engineers receive on the next send
  const handleResetAnnouncementFlag = async () => {
    if (!window.confirm('Reset the "already sent" flag on every engineer? They\'ll all receive the announcement again on the next Send. Use this if previous sends went to spam and you want to resend from a better address.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/announce-free-launch/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      window.alert(response.data.message || 'Reset done.');
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      let detail = data?.error || data?.detail;
      if (Array.isArray(detail)) {
        detail = detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join('; ');
      } else if (detail && typeof detail === 'object') {
        detail = JSON.stringify(detail);
      }
      window.alert(detail ? `Error ${status}: ${detail}` : `Network error: ${error.message}`);
    }
  };

  // Send a preview of Dan's founder announcement to the admin inbox — no engineer touched
  const handlePreviewFreeAnnouncement = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/announce-free-launch/preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      window.alert(response.data.message || 'Preview sent.');
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      let detail = data?.error || data?.detail;
      if (Array.isArray(detail)) {
        detail = detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join('; ');
      } else if (detail && typeof detail === 'object') {
        detail = JSON.stringify(detail);
      }
      window.alert(detail ? `Error ${status}: ${detail}` : `Network error: ${error.message}`);
    }
  };

  // Send Dan's founder announcement to every engineer (active/approved/trial_expired/suspended).
  // Auth: admin JWT only. Skips engineers already sent.
  const handleSendFreeAnnouncement = async () => {
    if (!window.confirm('Send the "now FREE" founder announcement to every engineer (active, approved, trial-expired, suspended)? Engineers already sent will be skipped.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/announce-free-launch`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 }
      );
      window.alert(response.data.message || 'Sent.');
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      let detail = data?.error || data?.detail;
      if (Array.isArray(detail)) {
        detail = detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join('; ');
      } else if (detail && typeof detail === 'object') {
        detail = JSON.stringify(detail);
      }
      const rawText = typeof data === 'string' ? data.slice(0, 200) : '';
      const msg = detail
        ? `Error ${status}: ${detail}`
        : status
          ? `HTTP ${status} ${error.response?.statusText || ''}${rawText ? ' — ' + rawText : ''}`
          : `Network error: ${error.message || 'Could not reach the server.'}`;
      window.alert(msg);
    }
  };

  const handleSendCustomEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.message.trim()) {
      toast({ title: "Error", description: "Subject and message are required", variant: "destructive" });
      return;
    }
    const recipientLabels = { all: 'all engineers', active: 'active engineers', approved: 'approved engineers', trial_expired: 'engineers with expired trials' };
    const isGroup = ['all', 'active', 'approved', 'trial_expired'].includes(emailForm.recipients);
    const selectedEngineer = !isGroup ? engineers.find(e => e.id === emailForm.recipients) : null;
    const confirmMsg = isGroup 
      ? `Send this email to ${recipientLabels[emailForm.recipients]}?` 
      : `Send this email to ${selectedEngineer?.fullName || 'this engineer'}?`;
    if (!window.confirm(confirmMsg)) return;
    
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/send-custom-email`, emailForm, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      window.alert(response.data.message || 'Emails sent!');
      setEmailModal(false);
      setEmailForm({ subject: '', message: '', recipients: 'all' });
      setEmailTemplate('custom');
    } catch (error) {
      window.alert('Error: ' + (error.response?.data?.detail || error.message || 'Failed to send emails'));
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: 'warning', icon: Clock },
      approved: { variant: 'default', icon: CheckCircle },
      active: { variant: 'success', icon: Users },
      suspended: { variant: 'destructive', icon: AlertCircle },
      trial_expired: { variant: 'destructive', icon: Clock }
    };
    const { variant, icon: Icon } = config[status] || { variant: 'secondary', icon: Clock };
    return (
      <Badge variant={variant} className="status-badge">
        <Icon size={12} /> {status === 'trial_expired' ? 'TRIAL EXPIRED' : status.toUpperCase()}
      </Badge>
    );
  };

  // Calculate stats from engineers
  const stats = {
    pending: engineers.filter(e => e.status === 'pending').length,
    approved: engineers.filter(e => e.status === 'approved').length,
    active: engineers.filter(e => e.status === 'active').length,
    suspended: engineers.filter(e => e.status === 'suspended').length,
    trial_expired: engineers.filter(e => e.status === 'trial_expired').length
  };

  // Count active engineers on trial
  const activeTrials = engineers.filter(e => e.status === 'active' && e.trialActive).length;

  // Calculate max for chart scaling
  const maxReferral = Math.max(...referrals.map(r => r.total), 1);

  return (
    <div className="admin-dashboard-new">
      {/* Header */}
      <header className="admin-header-new">
        <div className="admin-header-content">
          <div className="admin-brand">
            <img 
              src="https://customer-assets.emergentagent.com/job_trustygas/artifacts/191wqlf3_file_000000004ca871f48b719ce9d2a32667.png" 
              alt="The Gas Guide"
              className="admin-logo"
            />
            <span className="admin-title-text">Admin Dashboard</span>
          </div>
          <div className="admin-header-actions">
            <Button 
              onClick={async () => { setRefreshing(true); await fetchAllData(); setRefreshing(false); }}
              variant="outline" 
              size="sm"
              disabled={refreshing}
              data-testid="admin-refresh-btn"
              title="Reload all dashboard data"
            >
              <RefreshCw size={16} className={refreshing ? 'admin-refresh-spin' : ''} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
        {fetchFailedCount > 0 && (
          <div className="admin-fetch-warning" data-testid="admin-fetch-warning">
            <span>⚠️ Some sections didn&apos;t load ({fetchFailedCount} failed). Hit Refresh to try again.</span>
          </div>
        )}
      </header>

      <div className="admin-layout">
        {/* Sidebar Navigation */}
        <aside className="admin-sidebar">
          <nav className="admin-nav">
            <button 
              className={`nav-item ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveSection('overview')}
            >
              <Activity size={20} />
              <span>Overview</span>
            </button>
            <button 
              className={`nav-item ${activeSection === 'engineers' ? 'active' : ''}`}
              onClick={() => setActiveSection('engineers')}
            >
              <UserPlus size={20} />
              <span>Engineer Sign-ups</span>
              {stats.pending > 0 && <Badge variant="warning" className="nav-badge">{stats.pending}</Badge>}
            </button>
            <button 
              className={`nav-item ${activeSection === 'requests' ? 'active' : ''}`}
              onClick={() => setActiveSection('requests')}
            >
              <Search size={20} />
              <span>Postcode Searches</span>
              {hotspots.length > 0 && <Badge variant="secondary" className="nav-badge">{hotspots.length}</Badge>}
            </button>
            <button 
              className={`nav-item ${activeSection === 'marketing' ? 'active' : ''}`}
              onClick={() => setActiveSection('marketing')}
            >
              <Megaphone size={20} />
              <span>Marketing Analytics</span>
            </button>
            <button 
              className={`nav-item ${activeSection === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveSection('reviews')}
            >
              <Star size={20} />
              <span>Reviews</span>
              {reviewCounts.pending > 0 && <Badge variant="warning" className="nav-badge">{reviewCounts.pending}</Badge>}
            </button>
            <button 
              className={`nav-item ${activeSection === 'enquiries' ? 'active' : ''}`}
              onClick={() => setActiveSection('enquiries')}
            >
              <MessageSquare size={20} />
              <span>Enquiries</span>
              {contacts.filter(c => c.status !== 'contacted').length > 0 && <Badge variant="secondary" className="nav-badge">{contacts.filter(c => c.status !== 'contacted').length}</Badge>}
            </button>
            <button
              className={`nav-item ${activeSection === 'map' ? 'active' : ''}`}
              onClick={() => setActiveSection('map')}
            >
              <MapPin size={20} />
              <span>Engineer Map</span>
            </button>
            <button
              className={`nav-item ${activeSection === 'partners' ? 'active' : ''}`}
              onClick={() => setActiveSection('partners')}
              data-testid="nav-partners"
            >
              <Briefcase size={20} />
              <span>Partners</span>
              {partnerEnquiries.filter(p => (p.status || 'new') === 'new').length > 0 && (
                <Badge variant="warning" className="nav-badge">{partnerEnquiries.filter(p => (p.status || 'new') === 'new').length}</Badge>
              )}
            </button>
            <button
              className={`nav-item ${activeSection === 'campaigns' ? 'active' : ''}`}
              onClick={() => setActiveSection('campaigns')}
              data-testid="nav-campaigns"
            >
              <Megaphone size={20} />
              <span>Campaigns</span>
            </button>
            <button
              className={`nav-item ${activeSection === 'canned' ? 'active' : ''}`}
              onClick={() => setActiveSection('canned')}
              data-testid="nav-canned"
            >
              <FileText size={20} />
              <span>Canned Responses</span>
              {cannedResponses.length > 0 && <Badge variant="secondary" className="nav-badge">{cannedResponses.length}</Badge>}
            </button>
            <button
              className={`nav-item ${activeSection === 'analytics' ? 'active' : ''}`}
              onClick={() => { setActiveSection('analytics'); if (!analytics) fetchAnalytics(); }}
              data-testid="nav-analytics"
            >
              <BarChart3 size={20} />
              <span>Analytics</span>
            </button>
            <button
              className={`nav-item ${activeSection === 'actions' ? 'active' : ''}`}
              onClick={() => setActiveSection('actions')}
            >
              <Settings size={20} />
              <span>Actions</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-main">
          {loading && (
            <div className="admin-loading-inline" data-testid="dashboard-loading">
              <div className="loading-spinner"></div>
              <p>Loading dashboard data…</p>
              <span className="loading-hint">This can take up to 30 seconds the first time. The sidebar is already usable — click any tab.</span>
            </div>
          )}

          {/* Overview Section */}
          {activeSection === 'overview' && (
            <div className="admin-section">
              <h2 className="section-title"><Activity size={24} /> Overview</h2>
              
              <div className="overview-stats-grid">
                <Card className="stat-card-new stat-pending" onClick={() => { setActiveSection('engineers'); setEngineerTab('pending'); }}>
                  <CardContent>
                    <div className="stat-icon"><Clock size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-number">{stats.pending}</span>
                      <span className="stat-label">Pending Review</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="stat-card-new stat-approved" onClick={() => { setActiveSection('engineers'); setEngineerTab('approved'); }}>
                  <CardContent>
                    <div className="stat-icon"><CheckCircle size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-number">{stats.approved}</span>
                      <span className="stat-label">Approved (No Trial)</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="stat-card-new stat-active" onClick={() => { setActiveSection('engineers'); setEngineerTab('active'); }}>
                  <CardContent>
                    <div className="stat-icon"><Users size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-number">{stats.active}</span>
                      <span className="stat-label">Active Engineers</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="stat-card-new stat-requests" onClick={() => setActiveSection('requests')}>
                  <CardContent>
                    <div className="stat-icon"><MapPin size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-number">{hotspots.reduce((sum, h) => sum + h.requestCount, 0)}</span>
                      <span className="stat-label">Area Requests</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="stat-card-new stat-enquiries" onClick={() => setActiveSection('enquiries')}>
                  <CardContent>
                    <div className="stat-icon"><Mail size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-number">{contacts.filter(c => c.status !== 'contacted').length}</span>
                      <span className="stat-label">New Enquiries</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="stat-card-new stat-marketing" onClick={() => setActiveSection('marketing')}>
                  <CardContent>
                    <div className="stat-icon"><BarChart3 size={28} /></div>
                    <div className="stat-info">
                      <span className="stat-number">{referrals.length}</span>
                      <span className="stat-label">Marketing Sources</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              {stats.pending > 0 && (
                <Card className="quick-action-card">
                  <CardContent>
                    <AlertCircle size={20} className="alert-icon" />
                    <span>You have <strong>{stats.pending}</strong> pending engineer application{stats.pending !== 1 ? 's' : ''} to review</span>
                    <Button size="sm" onClick={() => { setActiveSection('engineers'); setEngineerTab('pending'); }}>
                      Review Now
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Engineers Section */}
          {activeSection === 'engineers' && (
            <div className="admin-section">
              <h2 className="section-title"><UserPlus size={24} /> Engineer Sign-ups</h2>
              
              {/* Engineer Tabs */}
              <div className="engineer-tabs">
                <button 
                  className={`tab-btn ${engineerTab === 'pending' ? 'active' : ''}`}
                  onClick={() => setEngineerTab('pending')}
                >
                  <Clock size={16} /> Pending ({stats.pending})
                </button>
                <button 
                  className={`tab-btn ${engineerTab === 'approved' ? 'active' : ''}`}
                  onClick={() => setEngineerTab('approved')}
                >
                  <CheckCircle size={16} /> Approved ({stats.approved})
                </button>
                <button 
                  className={`tab-btn ${engineerTab === 'active' ? 'active' : ''}`}
                  onClick={() => setEngineerTab('active')}
                >
                  <Users size={16} /> Active ({stats.active})
                </button>
                {stats.trial_expired > 0 && (
                  <button 
                    className={`tab-btn ${engineerTab === 'trial_expired' ? 'active' : ''}`}
                    onClick={() => setEngineerTab('trial_expired')}
                  >
                    <AlertCircle size={16} /> Trial Expired ({stats.trial_expired})
                  </button>
                )}
              </div>

              {/* Search bar — searches across ALL statuses so you never have to guess the tab */}
              <div className="admin-engineer-search" data-testid="admin-engineer-search">
                <Search size={16} className="admin-engineer-search-icon" />
                <input
                  type="text"
                  className="admin-engineer-search-input"
                  placeholder="Search by name, business, email, Gas Safe number or postcode…"
                  value={engineerSearch}
                  onChange={(e) => setEngineerSearch(e.target.value)}
                  data-testid="admin-engineer-search-input"
                />
                {engineerSearch && (
                  <button
                    type="button"
                    className="admin-engineer-search-clear"
                    onClick={() => setEngineerSearch('')}
                    data-testid="admin-engineer-search-clear"
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Engineer Cards */}
              <div className="engineers-grid">
                {engineerSearch.trim() && (
                  <div className="admin-engineer-search-hint" data-testid="admin-engineer-search-hint">
                    Showing matches across all statuses for &quot;{engineerSearch.trim()}&quot;.
                  </div>
                )}
                {engineers.filter(e => {
                  const q = engineerSearch.trim().toLowerCase();
                  // Only tab-filter when NOT searching
                  if (!q) return e.status === engineerTab;
                  const hay = [
                    e.fullName, e.businessName, e.email, e.phone,
                    e.gasSafeNumber, e.businessPostcode,
                    ...(Array.isArray(e.postcodes) ? e.postcodes : [])
                  ].filter(Boolean).join(' ').toLowerCase();
                  return hay.includes(q);
                }).map(engineer => (
                  <Card key={engineer.id} className="engineer-card-new">
                    <CardHeader>
                      <div className="engineer-card-header">
                        <div>
                          <CardTitle>{engineer.fullName}</CardTitle>
                          <p className="business-name">{engineer.businessName}</p>
                        </div>
                        <div className="header-actions">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditModal(engineer)}
                            className="btn-edit-engineer"
                          >
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setResetPasswordModal({ open: true, engineer })}
                            className="btn-reset-password"
                            title="Reset Password"
                          >
                            <Key size={14} />
                          </Button>
                          {getStatusBadge(engineer.status)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="engineer-details-grid">
                        <div className="detail-item">
                          <span className="detail-label">Gas Safe</span>
                          <span className="detail-value">{engineer.gasSafeNumber}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Experience</span>
                          <span className="detail-value">{engineer.yearsTrading} years</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Location</span>
                          <span className="detail-value">{engineer.businessPostcode}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Coverage</span>
                          <span className="detail-value">{engineer.postcodes?.slice(0,3).join(', ')}{engineer.postcodes?.length > 3 ? `... (${engineer.postcodes.length} total)` : ''}</span>
                        </div>
                      </div>
                      
                      {/* Area Density (informational only now — no limit) */}
                      {engineer.areaDensity?.checked && (
                        <div className="area-density-badge ok" data-testid="area-density">
                          <MapPin size={14} />
                          <span>
                            <strong>{engineer.areaDensity.nearbyCount}</strong> engineer{engineer.areaDensity.nearbyCount !== 1 ? 's' : ''} within 7.5 miles
                          </span>
                          {engineer.areaDensity.nearbyEngineers?.length > 0 && (
                            <span className="density-nearby">
                              ({engineer.areaDensity.nearbyEngineers.map(n => `${n.name} ${n.distance}mi`).join(', ')})
                            </span>
                          )}
                        </div>
                      )}

                      {/* Login Activity */}
                      <div className="engineer-login-activity">
                        <div className={`login-status ${engineer.loginCount > 0 ? 'has-logged-in' : 'never-logged-in'}`}>
                          {engineer.loginCount > 0 ? (
                            <>
                              <CheckCircle size={14} />
                              <span>Logged in {engineer.loginCount}x</span>
                              {engineer.lastLoginAt && (
                                <span className="last-login">
                                  (Last: {new Date(engineer.lastLoginAt).toLocaleDateString('en-GB')})
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              <AlertCircle size={14} />
                              <span>Never logged in</span>
                            </>
                          )}
                        </div>
                        {(engineer.status === 'active' || engineer.status === 'approved') && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleResendLogin(engineer)}
                            disabled={resendingLogin === engineer.id}
                            data-testid={`resend-login-${engineer.id}`}
                            style={{ fontSize: '12px', padding: '4px 10px', height: 'auto' }}
                          >
                            <Mail size={13} style={{ marginRight: '4px' }} />
                            {resendingLogin === engineer.id ? 'Sending...' : 'Resend Login'}
                          </Button>
                        )}
                      </div>
                      
                      {/* Email Read Receipts */}
                      {emailHistory[engineer.id]?.length > 0 && (
                        <div className="email-tracking-list">
                          {emailHistory[engineer.id].slice(0, 3).map((em, i) => (
                            <div key={i} className={`email-track-item ${em.openCount > 0 ? 'opened' : 'unopened'}`}>
                              <span className="email-track-type">{em.emailType === 'approval' ? 'Approval' : em.emailType === 'resend_login' ? 'Login Resent' : 'Email'}</span>
                              <span className="email-track-date">{new Date(em.sentAt).toLocaleDateString('en-GB')}</span>
                              {em.openCount > 0 ? (
                                <span className="email-track-status read"><Eye size={12} /> Opened {em.openCount}x</span>
                              ) : (
                                <span className="email-track-status unread"><EyeOff size={12} /> Not opened</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {!emailHistory[engineer.id] && (engineer.status === 'active' || engineer.status === 'approved') && (
                        <button 
                          className="email-history-load"
                          onClick={() => fetchEmailHistory(engineer.id)}
                          style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: '12px', cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <Mail size={12} /> Check email read status
                        </button>
                      )}
                      
                      {/* Trial Status Indicator */}
                      {engineer.trialActive && engineer.trialEndDate && (
                        <div className="trial-indicator">
                          <Clock size={14} />
                          <span>
                            Free Trial: <strong>{getTrialDaysRemaining(engineer)} days left</strong>
                            {' '}(ends {new Date(engineer.trialEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })})
                          </span>
                        </div>
                      )}
                      {engineer.status === 'trial_expired' && (
                        <div className="trial-indicator expired">
                          <AlertCircle size={14} />
                          <span>Trial expired on {new Date(engineer.trialEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                      )}
                      
                      <div className="engineer-contact">
                        <a href={`mailto:${engineer.email}`}>{engineer.email}</a>
                        <a href={`tel:${engineer.phone}`}>{engineer.phone}</a>
                      </div>

                      {engineer.services?.length > 0 && (
                        <div className="engineer-services-tags">
                          {engineer.services.map((s, i) => (
                            <Badge key={i} variant="outline">{s}</Badge>
                          ))}
                        </div>
                      )}

                      {engineer.referralSource && (
                        <div className="referral-tag">
                          <Megaphone size={14} />
                          <span>Found via: <strong>{REFERRAL_LABELS[engineer.referralSource] || engineer.referralSource}</strong></span>
                        </div>
                      )}

                      {/* Documents Toggle */}
                      {(engineer.hasInsuranceDoc || (engineer.workImagesCount || 0) > 0 || engineer.insuranceDoc || engineer.workImages?.length > 0) && (
                        <>
                          <Button 
                            variant="outline" 
                            className="view-docs-btn"
                            onClick={() => toggleEngineerExpand(engineer.id)}
                          >
                            {expandedEngineers[engineer.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            {expandedEngineers[engineer.id] ? 'Hide' : 'View'} Documents
                            <Badge variant="secondary">{(engineer.hasInsuranceDoc || engineer.insuranceDoc ? 1 : 0) + (engineer.workImagesCount ?? engineer.workImages?.length ?? 0)}</Badge>
                          </Button>
                          
                          {expandedEngineers[engineer.id] && (
                            <div className="engineer-documents">
                              {!engineer._documentsLoaded && (engineer.hasInsuranceDoc || (engineer.workImagesCount || 0) > 0) && !engineer.insuranceDoc && !engineer.workImages?.length && (
                                <p style={{ color: '#6b7280', fontSize: 14, padding: '8px 0' }}>Loading documents…</p>
                              )}
                              {engineer.insuranceDoc && (
                                <div className="doc-section">
                                  <h4><Shield size={16} /> Insurance</h4>
                                  <div 
                                    className="doc-thumb-wrapper"
                                    onClick={() => setImageViewer({ open: true, src: engineer.insuranceDoc, title: 'Insurance Document' })}
                                  >
                                    <img src={engineer.insuranceDoc} alt="Insurance" className="doc-thumb" />
                                  </div>
                                </div>
                              )}
                              {engineer.workImages?.length > 0 && (
                                <div className="doc-section">
                                  <h4><Image size={16} /> Work Photos</h4>
                                  <div className="doc-grid">
                                    {engineer.workImages.map((img, idx) => (
                                      <div 
                                        key={idx} 
                                        className="doc-thumb-wrapper"
                                        onClick={() => setImageViewer({ open: true, src: img, title: `Work Photo ${idx + 1}` })}
                                      >
                                        <img src={img} alt={`Work ${idx + 1}`} className="doc-thumb" />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* Actions */}
                      {engineerTab === 'pending' && (
                        <div className="engineer-actions">
                          <Button onClick={() => handleApprove(engineer.id)} className="btn-approve">
                            <CheckCircle size={16} /> Approve
                          </Button>
                          <Button onClick={() => handleReject(engineer.id)} variant="outline">
                            <XCircle size={16} /> Reject
                          </Button>
                          <Button 
                            onClick={() => setTrialModal({ open: true, engineer })} 
                            variant="outline"
                            className="btn-trial"
                          >
                            <Clock size={16} /> Free Trial
                          </Button>
                          <Button onClick={() => handleDelete(engineer.id, engineer.fullName)} variant="ghost" size="sm">
                            Delete
                          </Button>
                        </div>
                      )}
                      
                      {engineerTab === 'approved' && (
                        <div className="engineer-actions">
                          <p className="status-note awaiting">Awaiting payment to go live</p>
                          <Button 
                            onClick={() => setTrialModal({ open: true, engineer })} 
                            variant="outline"
                            size="sm"
                            className="btn-trial"
                          >
                            <Clock size={16} /> Free Trial
                          </Button>
                          {!engineer.loginCount && (
                            <Button 
                              onClick={() => handleSendReminder(engineer.id, engineer.fullName)} 
                              variant="outline" 
                              size="sm"
                              className="btn-reminder"
                            >
                              <Mail size={14} /> Send Reminder
                            </Button>
                          )}
                          <Button 
                            onClick={() => handleDeleteEngineer(engineer.id, engineer.fullName)} 
                            variant="ghost" 
                            size="sm"
                            className="btn-delete-engineer"
                          >
                            <Trash2 size={14} /> Remove
                          </Button>
                        </div>
                      )}
                      
                      {engineerTab === 'active' && (
                        <div className="engineer-actions">
                          <p className="status-note live">
                            {engineer.trialActive ? '⏱ Live (Free Trial)' : '✓ Live on directory'}
                          </p>
                          <Button 
                            onClick={() => handleDeleteEngineer(engineer.id, engineer.fullName)} 
                            variant="ghost" 
                            size="sm"
                            className="btn-delete-engineer"
                          >
                            <Trash2 size={14} /> Remove
                          </Button>
                        </div>
                      )}
                      
                      {engineerTab === 'trial_expired' && (
                        <div className="engineer-actions">
                          <p className="status-note expired">Trial has ended - no longer live</p>
                          <Button 
                            onClick={() => setTrialModal({ open: true, engineer })} 
                            variant="outline"
                            size="sm"
                            className="btn-trial"
                          >
                            <Clock size={16} /> Extend Trial
                          </Button>
                          <Button 
                            onClick={() => handleDeleteEngineer(engineer.id, engineer.fullName)} 
                            variant="ghost" 
                            size="sm"
                            className="btn-delete-engineer"
                          >
                            <Trash2 size={14} /> Remove
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                
                {(() => {
                  const q = engineerSearch.trim().toLowerCase();
                  if (q) {
                    const matches = engineers.filter(e => {
                      const hay = [
                        e.fullName, e.businessName, e.email, e.phone,
                        e.gasSafeNumber, e.businessPostcode,
                        ...(Array.isArray(e.postcodes) ? e.postcodes : [])
                      ].filter(Boolean).join(' ').toLowerCase();
                      return hay.includes(q);
                    });
                    if (matches.length === 0) {
                      return (
                        <div className="empty-state-new" data-testid="admin-engineer-search-empty">
                          <Users size={48} />
                          <p>No engineers match &quot;{engineerSearch}&quot;.</p>
                        </div>
                      );
                    }
                    return null;
                  }
                  if (engineers.filter(e => e.status === engineerTab).length === 0) {
                    return (
                      <div className="empty-state-new">
                        <Users size={48} />
                        <p>No {engineerTab} engineers</p>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          {/* Postcode Requests Section */}
          {activeSection === 'requests' && (
            <div className="admin-section">
              <h2 className="section-title"><Search size={24} /> Postcode Searches</h2>
              <p className="section-description">
                Every postcode searched on your site. Red highlights show searches with no engineers — target these areas for recruitment.
              </p>
              
              {/* Live Search Stats */}
              {searchAnalytics.totalSearches > 0 && (
                <div className="admin-map-stats" style={{ marginBottom: '20px' }}>
                  <div className="admin-map-stat">
                    <Search size={16} />
                    <strong>{searchAnalytics.totalSearches}</strong> total searches
                  </div>
                  <div className="admin-map-stat" style={{ color: '#dc2626' }}>
                    <AlertCircle size={16} />
                    <strong>{searchAnalytics.noResultTotal}</strong> with no results
                  </div>
                </div>
              )}

              {/* Search Demand Map */}
              {searchAnalytics.searches?.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1e293b' }}>
                    <MapPin size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Search Demand Map
                  </h3>
                  <SearchDemandMap searches={searchAnalytics.searches} />
                </div>
              )}

              {/* Live Search Data */}
              {searchAnalytics.searches?.length > 0 && (
                <>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1e293b' }}>
                    <Activity size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Live Search Activity
                  </h3>
                  <div className="hotspots-list" style={{ marginBottom: '32px' }}>
                    {searchAnalytics.searches.map((s, index) => (
                      <Card key={s.postcode} className={`hotspot-card-new ${s.noResultSearches > 0 && !s.dealtWith ? 'top-hotspot' : ''}`} style={s.dealtWith ? { opacity: 0.55 } : {}}>
                        <CardContent>
                          <div className="hotspot-rank">#{index + 1}</div>
                          <div className="hotspot-details">
                            <span className="hotspot-postcode">
                              {s.postcode}
                              {s.dealtWith && <CheckCircle size={14} style={{ color: '#22c55e', marginLeft: '6px', verticalAlign: 'middle' }} />}
                            </span>
                            <span className="hotspot-meta">
                              {s.totalSearches} search{s.totalSearches !== 1 ? 'es' : ''}
                              {s.noResultSearches > 0 && (
                                <span style={{ color: '#dc2626', fontWeight: 600 }}> • {s.noResultSearches} with no results</span>
                              )}
                              {s.lastSearch && ` • Last: ${new Date(s.lastSearch).toLocaleDateString('en-GB')}`}
                              {s.dealtWith && <span style={{ color: '#22c55e', fontWeight: 600 }}> • Dealt with</span>}
                            </span>
                          </div>
                          {s.noResultSearches > 0 && !s.dealtWith && <AlertCircle size={20} style={{ color: '#dc2626' }} />}
                          <Button
                            variant={s.dealtWith ? "outline" : "default"}
                            size="sm"
                            onClick={() => handleToggleDealtWith(s.postcode)}
                            data-testid={`dealt-btn-${s.postcode}`}
                            style={{ minWidth: '100px', fontSize: '12px' }}
                          >
                            {s.dealtWith ? 'Undo' : 'Dealt With'}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}

              {/* Form Requests (existing) */}
              {hotspots.length > 0 && (
                <>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1e293b' }}>
                    <Mail size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Engineer Requests (form submissions)
                  </h3>
                  <div className="hotspots-list">
                    {hotspots.map((spot, index) => (
                      <Card key={spot.postcode} className={`hotspot-card-new ${index < 3 ? 'top-hotspot' : ''}`}>
                        <CardContent>
                          <div className="hotspot-rank">#{index + 1}</div>
                          <div className="hotspot-details">
                            <span className="hotspot-postcode">{spot.postcode}</span>
                            <span className="hotspot-meta">
                              {spot.requestCount} request{spot.requestCount !== 1 ? 's' : ''}
                              {spot.lastRequest && ` • Last: ${new Date(spot.lastRequest).toLocaleDateString('en-GB')}`}
                            </span>
                          </div>
                          {index < 3 && <TrendingUp className="trending-icon" size={24} />}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="hotspot-delete-btn"
                            onClick={() => handleDeleteAreaRequest(spot.postcode)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}

              {hotspots.length === 0 && searchAnalytics.searches?.length === 0 && (
                <div className="empty-state-new">
                  <MapPin size={48} />
                  <p>No postcode searches yet</p>
                </div>
              )}
            </div>
          )}

          {/* Marketing Analytics Section */}
          {activeSection === 'marketing' && (
            <div className="admin-section">
              <h2 className="section-title"><Megaphone size={24} /> Marketing Analytics</h2>
              <p className="section-description">
                See how people are finding The Gas Guide. Use this to focus your advertising budget.
              </p>
              
              {referrals.length > 0 ? (
                <>
                  {/* Summary Cards */}
                  <div className="marketing-summary">
                    <Card className="summary-card">
                      <CardContent>
                        <span className="summary-number">{referrals.reduce((sum, r) => sum + r.total, 0)}</span>
                        <span className="summary-label">Total Responses</span>
                      </CardContent>
                    </Card>
                    <Card className="summary-card">
                      <CardContent>
                        <span className="summary-number">{referrals.reduce((sum, r) => sum + r.engineerSignups, 0)}</span>
                        <span className="summary-label">Engineer Sign-ups</span>
                      </CardContent>
                    </Card>
                    <Card className="summary-card">
                      <CardContent>
                        <span className="summary-number">{referrals.reduce((sum, r) => sum + r.publicRequests, 0)}</span>
                        <span className="summary-label">Public Requests</span>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Bar Chart */}
                  <Card className="chart-card">
                    <CardHeader>
                      <CardTitle><BarChart3 size={20} /> Traffic Sources</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bar-chart">
                        {referrals.map((ref, index) => (
                          <div key={ref.source} className="bar-row">
                            <div className="bar-label">{REFERRAL_LABELS[ref.source] || ref.source}</div>
                            <div className="bar-container">
                              <div 
                                className="bar-fill"
                                style={{ 
                                  width: `${(ref.total / maxReferral) * 100}%`,
                                  backgroundColor: CHART_COLORS[index % CHART_COLORS.length]
                                }}
                              >
                                <span className="bar-value">{ref.total}</span>
                              </div>
                            </div>
                            <div className="bar-breakdown">
                              <span className="breakdown-engineers" title="Engineer sign-ups">{ref.engineerSignups} eng</span>
                              <span className="breakdown-requests" title="Public requests">{ref.publicRequests} req</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top Performers */}
                  {referrals.length >= 3 && (
                    <Card className="top-performers-card">
                      <CardHeader>
                        <CardTitle>🏆 Top 3 Performers</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="top-performers">
                          {referrals.slice(0, 3).map((ref, index) => (
                            <div key={ref.source} className={`performer performer-${index + 1}`}>
                              <span className="performer-rank">{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                              <span className="performer-name">{REFERRAL_LABELS[ref.source] || ref.source}</span>
                              <span className="performer-count">{ref.total}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <div className="empty-state-new">
                  <BarChart3 size={48} />
                  <p>No marketing data yet</p>
                  <span>Data will appear once people start responding to "How did you hear about us?"</span>
                </div>
              )}
            </div>
          )}

          {/* Enquiries Section */}
          {activeSection === 'enquiries' && (
            <div className="admin-section">
              <h2 className="section-title"><MessageSquare size={24} /> Enquiries</h2>
              
              {contacts.filter(c => c.status !== 'contacted').length > 0 ? (
                <div className="enquiries-list">
                  {contacts.filter(c => c.status !== 'contacted').map(contact => (
                    <Card key={contact.id} className="enquiry-card">
                      <CardHeader>
                        <div className="enquiry-header">
                          <CardTitle>{contact.subject}</CardTitle>
                          <span className="enquiry-date">
                            {new Date(contact.createdAt).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="enquiry-from">
                          <strong>{contact.name}</strong>
                          <a href={`mailto:${contact.email}`}>{contact.email}</a>
                          {contact.phone && <a href={`tel:${contact.phone}`}>{contact.phone}</a>}
                        </div>
                        <p className="enquiry-message">{contact.message}</p>
                        <div className="enquiry-actions">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openReplyModal(contact.email, contact.name, `Re: ${contact.subject}`)}
                            data-testid={`enquiry-reply-${contact.id}`}
                          >
                            <Mail size={14} /> Reply
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleMarkContacted(contact.id)}
                            className="btn-mark-contacted"
                          >
                            <CheckCircle size={14} /> Mark as Contacted
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="empty-state-new">
                  <Mail size={48} />
                  <p>No new enquiries</p>
                </div>
              )}

              {/* Show contacted enquiries collapsed */}
              {contacts.filter(c => c.status === 'contacted').length > 0 && (
                <div className="contacted-section">
                  <h3 className="contacted-title" onClick={() => setShowContacted(!showContacted)}>
                    {showContacted ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    Previously Contacted ({contacts.filter(c => c.status === 'contacted').length})
                  </h3>
                  {showContacted && (
                    <div className="enquiries-list contacted">
                      {contacts.filter(c => c.status === 'contacted').map(contact => (
                        <Card key={contact.id} className="enquiry-card contacted">
                          <CardHeader>
                            <div className="enquiry-header">
                              <CardTitle>{contact.subject}</CardTitle>
                              <Badge variant="secondary">Contacted</Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="enquiry-from">
                              <strong>{contact.name}</strong>
                              <a href={`mailto:${contact.email}`}>{contact.email}</a>
                            </div>
                            <p className="enquiry-message">{contact.message}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Reviews Moderation Section */}
          {activeSection === 'reviews' && (
            <div className="admin-section">
              <h2 className="section-title"><Star size={24} /> Review Moderation</h2>
              
              {/* Review Tabs */}
              <div className="engineer-tabs">
                <button 
                  className={`tab-btn ${reviewTab === 'pending' ? 'active' : ''}`}
                  onClick={() => setReviewTab('pending')}
                >
                  <Clock size={16} /> Pending ({reviewCounts.pending})
                </button>
                <button 
                  className={`tab-btn ${reviewTab === 'approved' ? 'active' : ''}`}
                  onClick={() => setReviewTab('approved')}
                >
                  <CheckCircle size={16} /> Approved ({reviewCounts.approved})
                </button>
                <button 
                  className={`tab-btn ${reviewTab === 'rejected' ? 'active' : ''}`}
                  onClick={() => setReviewTab('rejected')}
                >
                  <XCircle size={16} /> Rejected ({reviewCounts.rejected})
                </button>
              </div>

              {/* Reviews List */}
              <div className="reviews-admin-list">
                {reviews.filter(r => r.status === reviewTab).length > 0 ? (
                  reviews.filter(r => r.status === reviewTab).map(review => (
                    <Card key={review.id} className="review-admin-card">
                      <CardContent>
                        <div className="review-admin-header">
                          <div className="review-admin-engineer">
                            <span className="review-for-label">Review for:</span>
                            <strong>{review.engineerName || 'Unknown Engineer'}</strong>
                          </div>
                          <div className="review-admin-rating">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={16} fill={i < review.rating ? '#f59e0b' : 'none'} color={i < review.rating ? '#f59e0b' : '#e2e8f0'} />
                            ))}
                          </div>
                        </div>
                        
                        <div className="review-admin-customer">
                          <div className="customer-info">
                            <span className="customer-name">{review.customerName}</span>
                            <span className="customer-phone"><Phone size={14} /> {review.customerPhone}</span>
                          </div>
                          <span className="review-date">
                            {review.date ? new Date(review.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                          </span>
                        </div>
                        
                        <p className="review-admin-comment">"{review.comment}"</p>
                        
                        <div className="review-admin-actions">
                          {review.status === 'pending' && (
                            <>
                              <Button size="sm" onClick={() => handleApproveReview(review.id)} className="btn-approve">
                                <CheckCircle size={14} /> Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleRejectReview(review.id)} className="btn-reject">
                                <XCircle size={14} /> Reject
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteReview(review.id)} className="btn-delete">
                            <Trash2 size={14} /> Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="empty-state-new">
                    <Star size={48} />
                    <p>No {reviewTab} reviews</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Engineer Map Section */}
          {activeSection === 'map' && (
            <div className="admin-section" data-testid="map-section">
              <h2 className="section-title"><MapPin size={24} /> Engineer Map</h2>
              <p className="section-description">
                See where your engineers are based. Use this to target your advertising in areas with active engineers.
              </p>
              <AdminMap />
            </div>
          )}

          {/* Partner Enquiries Section */}
          {activeSection === 'partners' && (
            <div className="admin-section" data-testid="partners-section">
              <h2>Partner Enquiries</h2>
              <p style={{color: '#6b7280', marginBottom: '24px'}}>Sponsorship & advertising enquiries from the /partners page. Newest first.</p>
              {partnerEnquiries.length === 0 ? (
                <div className="empty-state-new">
                  <Briefcase size={48} />
                  <p>No partner enquiries yet.</p>
                </div>
              ) : (
                <div className="partner-enquiries-list">
                  {partnerEnquiries.map((p) => (
                    <div key={p._key} className={`partner-enquiry-card status-${p.status || 'new'}`} data-testid={`partner-enquiry-${p._key}`}>
                      <div className="partner-enquiry-header">
                        <div>
                          <h3>{p.company}</h3>
                          <p className="partner-enquiry-meta">
                            {p.contactName || 'No name'} · <a href={`mailto:${p.email}`}>{p.email}</a>
                            {p.phone ? ` · ${p.phone}` : ''}
                          </p>
                          <p className="partner-enquiry-date">
                            {p.createdAt ? new Date(p.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                            {p.interest ? ` · ${p.interest}` : ''}
                          </p>
                        </div>
                        <span className={`partner-enquiry-status ${p.status || 'new'}`}>{p.status || 'new'}</span>
                      </div>
                      <div className="partner-enquiry-message">{p.message}</div>
                      <div className="partner-enquiry-actions">
                        <Button variant="outline" size="sm" onClick={() => openReplyModal(p.email, p.contactName || '', `Re: Your enquiry to The Gas Guide`)} data-testid={`partner-reply-${p.email}`}>
                          <Mail size={14} /> Reply
                        </Button>
                        {(p.status || 'new') !== 'contacted' && (
                          <Button variant="outline" size="sm" onClick={async () => {
                            try {
                              const token = localStorage.getItem('admin_token');
                              await axios.post(`${API}/admin/partner-enquiries/update-status`, {
                                createdAt: p.createdAt, email: p.email, company: p.company, status: 'contacted'
                              }, { headers: { Authorization: `Bearer ${token}` } });
                              fetchAllData();
                            } catch (err) { window.alert('Failed to update status'); }
                          }}>
                            <CheckCircle size={14} /> Mark contacted
                          </Button>
                        )}
                        {(p.status || 'new') !== 'closed' && (
                          <Button variant="outline" size="sm" onClick={async () => {
                            try {
                              const token = localStorage.getItem('admin_token');
                              await axios.post(`${API}/admin/partner-enquiries/update-status`, {
                                createdAt: p.createdAt, email: p.email, company: p.company, status: 'closed'
                              }, { headers: { Authorization: `Bearer ${token}` } });
                              fetchAllData();
                            } catch (err) { window.alert('Failed to update status'); }
                          }}>
                            Close
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Email Campaigns Section */}
          {activeSection === 'campaigns' && (
            <div className="admin-section" data-testid="campaigns-section">
              <h2>Email Campaigns</h2>
              <p style={{color: '#6b7280', marginBottom: '24px'}}>Compose and send a broadcast to any group of engineers. Use <code>{'{firstName}'}</code> in the body to personalise.</p>

              <div className="campaign-composer">
                {cannedResponses.length > 0 && (
                  <div className="campaign-field">
                    <label>Insert canned response (optional)</label>
                    <select
                      className="campaign-select"
                      value=""
                      onChange={(e) => { if (e.target.value) applyCannedToCampaign(e.target.value); }}
                      data-testid="campaign-canned-picker"
                    >
                      <option value="">— Pick a template to load —</option>
                      {cannedResponses.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                    <span className="field-hint">Loads the subject + body. You can edit it before sending.</span>
                  </div>
                )}

                <div className="campaign-field">
                  <label>Audience</label>
                  <select
                    className="campaign-select"
                    value={campaign.audience}
                    onChange={(e) => setCampaign({...campaign, audience: e.target.value})}
                    data-testid="campaign-audience"
                  >
                    <option value="all">All engineers (active + approved + trial-expired)</option>
                    <option value="active">Active only (live listings)</option>
                    <option value="approved_never_logged_in">Approved but never logged in (nudge inactive)</option>
                    <option value="founding">Founding members only</option>
                    <option value="trial_expired">Trial expired</option>
                  </select>
                </div>

                <div className="campaign-field">
                  <label>Subject</label>
                  <input
                    type="text"
                    className="campaign-input"
                    placeholder="e.g. Big update — new features on The Gas Guide"
                    value={campaign.subject}
                    onChange={(e) => setCampaign({...campaign, subject: e.target.value})}
                    data-testid="campaign-subject"
                  />
                </div>

                <div className="campaign-field">
                  <label>Body (plain text — use <code>{'{firstName}'}</code> for personalisation)</label>
                  <textarea
                    className="campaign-textarea"
                    rows={12}
                    placeholder={"Write your message here. Example:\n\nGreat news — we've just added a new feature to your dashboard.\n\nIt does X, Y and Z.\n\nCheck it out at thegasguide.co.uk/engineer/dashboard\n\nCheers,\nDan"}
                    value={campaign.body}
                    onChange={(e) => setCampaign({...campaign, body: e.target.value})}
                    data-testid="campaign-body"
                  />
                </div>

                <div className="campaign-actions">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (!campaign.subject.trim() || !campaign.body.trim()) return window.alert('Subject and body are required.');
                      try {
                        const token = localStorage.getItem('admin_token');
                        const res = await axios.post(`${API}/admin/campaigns/preview`, campaign, { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
                        window.alert(res.data.message);
                      } catch (err) {
                        const detail = err.response?.data?.error || err.response?.data?.detail || err.message;
                        window.alert(`Error: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
                      }
                    }}
                    data-testid="campaign-preview-btn"
                  >
                    Preview to Admin Inbox
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!campaign.subject.trim() || !campaign.body.trim()) return window.alert('Subject and body are required.');
                      if (!window.confirm(`Send this campaign to the "${campaign.audience}" audience? Cannot be undone.`)) return;
                      setSendingCampaign(true);
                      try {
                        const token = localStorage.getItem('admin_token');
                        const res = await axios.post(`${API}/admin/campaigns/send`, campaign, { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 });
                        window.alert(res.data.message);
                      } catch (err) {
                        const detail = err.response?.data?.error || err.response?.data?.detail || err.message;
                        window.alert(`Error: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
                      } finally {
                        setSendingCampaign(false);
                      }
                    }}
                    disabled={sendingCampaign}
                    data-testid="campaign-send-btn"
                  >
                    {sendingCampaign ? 'Sending…' : 'Send Campaign'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Canned Responses Section */}
          {activeSection === 'canned' && (
            <div className="admin-section" data-testid="canned-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                <h2 className="section-title"><FileText size={24} /> Canned Responses</h2>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {cannedResponses.length === 0 && (
                    <Button variant="outline" onClick={handleSeedCanned} disabled={seedingCanned} data-testid="canned-seed-btn">
                      {seedingCanned ? 'Loading…' : 'Load starter templates'}
                    </Button>
                  )}
                  <Button
                    onClick={() => setCannedEditor({ open: true, mode: 'create', id: null, name: '', subject: '', body: '' })}
                    data-testid="canned-new-btn"
                  >
                    <Plus size={16} style={{ marginRight: 6 }} /> New response
                  </Button>
                </div>
              </div>

              <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: 14 }}>
                Reusable email templates you can drop into Campaigns, the Send Email modal, or a reply from Enquiries/Partners. Use <code>{'{firstName}'}</code> in the subject or body — it&apos;ll be swapped in when sending.
              </p>

              {cannedResponses.length === 0 ? (
                <div className="empty-state-new">
                  <FileText size={48} />
                  <p>No canned responses yet</p>
                  <span>Load the 6 starter templates or create your first response.</span>
                </div>
              ) : (
                <div className="canned-list">
                  {cannedResponses.map(r => (
                    <Card key={r.id} className="canned-card" data-testid={`canned-card-${r.id}`}>
                      <CardContent>
                        <div className="canned-card-header">
                          <div>
                            <h3>{r.name}</h3>
                            <div className="canned-subject">{r.subject}</div>
                          </div>
                          <div className="canned-actions">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCannedEditor({ open: true, mode: 'edit', id: r.id, name: r.name, subject: r.subject, body: r.body })}
                              data-testid={`canned-edit-${r.id}`}
                            >
                              <Edit size={14} /> Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteCanned(r.id)}
                              data-testid={`canned-delete-${r.id}`}
                            >
                              <Trash2 size={14} /> Delete
                            </Button>
                          </div>
                        </div>
                        <pre className="canned-body">{r.body}</pre>
                        <div className="canned-meta">
                          Used {r.usageCount || 0}×
                          {r.updatedAt ? ` · updated ${new Date(r.updatedAt).toLocaleDateString('en-GB')}` : ''}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Analytics Section */}
          {activeSection === 'analytics' && (
            <div className="admin-section" data-testid="analytics-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                <h2 className="section-title"><BarChart3 size={24} /> Analytics</h2>
                <Button variant="outline" onClick={fetchAnalytics} disabled={analyticsLoading} data-testid="analytics-refresh-btn">
                  <RefreshCw size={16} style={{ marginRight: 6 }} />
                  {analyticsLoading ? 'Refreshing…' : 'Refresh'}
                </Button>
              </div>

              {analyticsLoading && !analytics && (
                <p style={{ color: '#6b7280' }}>Loading analytics…</p>
              )}

              {analytics && (
                <>
                  {/* KPI cards */}
                  <div className="analytics-kpi-grid">
                    <Card className="analytics-kpi-card" data-testid="kpi-page-views">
                      <CardContent>
                        <div className="analytics-kpi-label">Page views (total)</div>
                        <div className="analytics-kpi-value">{analytics.pageViews?.total || 0}</div>
                        <div className="analytics-kpi-sub">
                          {analytics.pageViews?.last7d || 0} in last 7d · {analytics.pageViews?.last24h || 0} today
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="analytics-kpi-card" data-testid="kpi-searches">
                      <CardContent>
                        <div className="analytics-kpi-label">Postcode searches</div>
                        <div className="analytics-kpi-value">{analytics.searches?.total || 0}</div>
                        <div className="analytics-kpi-sub">
                          {analytics.searches?.noResult || 0} no-result · {analytics.searches?.last7d || 0} in 7d
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="analytics-kpi-card" data-testid="kpi-engineer-clicks">
                      <CardContent>
                        <div className="analytics-kpi-label">Engineer profile clicks</div>
                        <div className="analytics-kpi-value">{analytics.engineerClicks?.total || 0}</div>
                        <div className="analytics-kpi-sub">
                          {analytics.engineerClicks?.last7d || 0} in last 7d
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="analytics-kpi-card" data-testid="kpi-logins">
                      <CardContent>
                        <div className="analytics-kpi-label">Engineer logins</div>
                        <div className="analytics-kpi-value">{analytics.logins?.totalLogins || 0}</div>
                        <div className="analytics-kpi-sub">
                          {analytics.logins?.everLogged || 0}/{analytics.logins?.totalEngineers || 0} ever · {analytics.logins?.recentLogins7d || 0} in 7d
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="analytics-kpi-card" data-testid="kpi-never-logged">
                      <CardContent>
                        <div className="analytics-kpi-label">Never logged in</div>
                        <div className="analytics-kpi-value" style={{ color: (analytics.logins?.neverLogged || 0) > 0 ? '#dc2626' : '#059669' }}>
                          {analytics.logins?.neverLogged || 0}
                        </div>
                        <div className="analytics-kpi-sub">
                          approved/active engineers
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Grids: Page views + Top searches */}
                  <div className="analytics-two-col">
                    <Card className="analytics-panel">
                      <CardHeader><CardTitle>Page views by page</CardTitle></CardHeader>
                      <CardContent>
                        {(!analytics.pageViews?.byPage || analytics.pageViews.byPage.length === 0) ? (
                          <p style={{ color: '#6b7280', fontSize: 14 }}>No page views recorded yet. Views will start showing here once visitors land on tracked pages.</p>
                        ) : (
                          <table className="analytics-table" data-testid="page-views-table">
                            <thead>
                              <tr>
                                <th>Page</th>
                                <th style={{ textAlign: 'right' }}>Total</th>
                                <th style={{ textAlign: 'right' }}>7d</th>
                                <th style={{ textAlign: 'right' }}>24h</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analytics.pageViews.byPage.map((p) => (
                                <tr key={p.page}>
                                  <td>/{p.page === 'home' ? '' : p.page}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{p.total}</td>
                                  <td style={{ textAlign: 'right' }}>{p.last7d}</td>
                                  <td style={{ textAlign: 'right' }}>{p.last24h}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </CardContent>
                    </Card>

                    <Card className="analytics-panel">
                      <CardHeader><CardTitle>Top searched postcodes</CardTitle></CardHeader>
                      <CardContent>
                        {(!analytics.searches?.topSearches || analytics.searches.topSearches.length === 0) ? (
                          <p style={{ color: '#6b7280', fontSize: 14 }}>No searches yet.</p>
                        ) : (
                          <table className="analytics-table" data-testid="top-searches-table">
                            <thead>
                              <tr>
                                <th>Postcode</th>
                                <th style={{ textAlign: 'right' }}>Searches</th>
                                <th style={{ textAlign: 'right' }}>No result</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analytics.searches.topSearches.map((s) => (
                                <tr key={s.postcode}>
                                  <td style={{ fontWeight: 600 }}>{s.postcode}</td>
                                  <td style={{ textAlign: 'right' }}>{s.count}</td>
                                  <td style={{ textAlign: 'right', color: s.noResults > 0 ? '#dc2626' : '#059669' }}>{s.noResults}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Top engineers clicked */}
                  <Card className="analytics-panel" style={{ marginTop: 20 }}>
                    <CardHeader><CardTitle>Top engineers by profile clicks</CardTitle></CardHeader>
                    <CardContent>
                      {(!analytics.engineerClicks?.top || analytics.engineerClicks.top.length === 0) ? (
                        <p style={{ color: '#6b7280', fontSize: 14 }}>No engineer profile clicks yet. Click tracking fires when a homeowner taps &quot;View Details&quot; on the Find Engineer page.</p>
                      ) : (
                        <table className="analytics-table" data-testid="top-engineers-table">
                          <thead>
                            <tr>
                              <th>Engineer</th>
                              <th>Business</th>
                              <th>Status</th>
                              <th style={{ textAlign: 'right' }}>Clicks</th>
                              <th style={{ textAlign: 'right' }}>7d</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.engineerClicks.top.map((e) => (
                              <tr key={e.engineerId}>
                                <td>{e.name}</td>
                                <td style={{ color: '#6b7280' }}>{e.businessName || '—'}</td>
                                <td><Badge variant="outline">{e.status || '—'}</Badge></td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{e.clicks}</td>
                                <td style={{ textAlign: 'right' }}>{e.last7d}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </CardContent>
                  </Card>

                  {/* Never-logged-in engineers */}
                  <Card className="analytics-panel" style={{ marginTop: 20 }}>
                    <CardHeader><CardTitle>Engineers who have never logged in</CardTitle></CardHeader>
                    <CardContent>
                      {(!analytics.logins?.neverLoggedList || analytics.logins.neverLoggedList.length === 0) ? (
                        <p style={{ color: '#059669', fontSize: 14 }}>Every approved/active engineer has logged in at least once. Nice.</p>
                      ) : (
                        <table className="analytics-table" data-testid="never-logged-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Approved</th>
                              <th style={{ textAlign: 'right' }}>Reminders sent</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.logins.neverLoggedList.map((e) => (
                              <tr key={e.id}>
                                <td style={{ fontWeight: 600 }}>{e.name}</td>
                                <td style={{ color: '#6b7280' }}>{e.email}</td>
                                <td style={{ color: '#6b7280' }}>{e.approvalDate ? new Date(e.approvalDate).toLocaleDateString('en-GB') : '—'}</td>
                                <td style={{ textAlign: 'right' }}>{e.reminderCount || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </CardContent>
                  </Card>

                  <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 16 }}>
                    Snapshot generated {analytics.generatedAt ? new Date(analytics.generatedAt).toLocaleString('en-GB') : ''}
                  </p>
                </>
              )}
            </div>
          )}

          {/* Admin Actions Section */}
          {activeSection === 'actions' && (
            <div className="admin-section" data-testid="actions-section">
              <h2>Admin Actions</h2>
              <p style={{color: '#6b7280', marginBottom: '24px'}}>Bulk actions and admin tools. Use these carefully.</p>
              
              <div className="admin-actions-grid">
                {/* Migrate to FREE */}
                <Card className="admin-action-card" data-testid="migrate-free-card">
                  <CardContent>
                    <div className="admin-action-icon" style={{background: '#ecfdf5'}}>
                      <CheckCircle size={24} style={{color: '#059669'}} />
                    </div>
                    <div className="admin-action-info">
                      <h3>Migrate All Engineers to FREE</h3>
                      <p>One-click migration: convert every approved / active / trial-expired engineer to a permanent FREE listing. Clears trial dates, sets <strong>subscriptionType = free</strong>. Idempotent — safe to run twice.</p>
                    </div>
                    <Button onClick={handleMigrateAllToFree} data-testid="migrate-free-btn">
                      Migrate Everyone
                    </Button>
                  </CardContent>
                </Card>

                {/* Send Free Launch Announcement */}
                <Card className="admin-action-card" data-testid="announce-free-card">
                  <CardContent>
                    <div className="admin-action-icon" style={{background: '#fef3c7'}}>
                      <Megaphone size={24} style={{color: '#d97706'}} />
                    </div>
                    <div className="admin-action-info">
                      <h3>Send &quot;Now FREE&quot; Announcement</h3>
                      <p>Send Dan&apos;s founder announcement (&quot;The Gas Guide is changing — and it&apos;s good news&quot;) to every engineer on the platform — active, approved, trial-expired and suspended. Personalised with their first name. Engineers already sent will be skipped automatically.</p>
                    </div>
                    <div className="admin-action-button-row">
                      <Button variant="outline" onClick={handlePreviewFreeAnnouncement} data-testid="preview-free-btn">
                        Preview to Admin Inbox
                      </Button>
                      <Button variant="outline" onClick={handleResetAnnouncementFlag} data-testid="reset-free-btn">
                        Reset &quot;already sent&quot; flag
                      </Button>
                      <Button onClick={handleSendFreeAnnouncement} data-testid="announce-free-btn">
                        Send to All Engineers
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Send Login Reminders */}
                <Card className="admin-action-card" data-testid="send-reminders-card">
                  <CardContent>
                    <div className="admin-action-icon" style={{background: '#eff6ff'}}>
                      <Mail size={24} style={{color: '#2563eb'}} />
                    </div>
                    <div className="admin-action-info">
                      <h3>Send Login Reminders</h3>
                      <p>Email all engineers who haven&apos;t logged in yet with their login details.</p>
                    </div>
                    <Button onClick={handleSendLoginReminders} disabled={sendingReminders} data-testid="send-reminders-btn">
                      {sendingReminders ? 'Sending...' : 'Send Reminders'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Send Custom Email */}
                <Card className="admin-action-card" data-testid="send-email-card">
                  <CardContent>
                    <div className="admin-action-icon" style={{background: '#fdf2f8'}}>
                      <Megaphone size={24} style={{color: '#db2777'}} />
                    </div>
                    <div className="admin-action-info">
                      <h3>Send Email to Engineers</h3>
                      <p>Compose and send a custom email to all engineers, or a specific group (active, approved, trial expired).</p>
                    </div>
                    <Button onClick={() => setEmailModal(true)} data-testid="compose-email-btn">
                      Compose Email
                    </Button>
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <Card className="admin-action-card">
                  <CardContent>
                    <div className="admin-action-icon" style={{background: '#fefce8'}}>
                      <BarChart3 size={24} style={{color: '#ca8a04'}} />
                    </div>
                    <div className="admin-action-info">
                      <h3>Quick Stats</h3>
                      <p>Total engineers: <strong>{engineers.length}</strong> | Active: <strong>{stats.active}</strong> | Founding Members: <strong>{engineers.filter(e => e.isFoundingMember).length}</strong></p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* Image Viewer Modal */}
      {imageViewer.open && (
        <div className="image-viewer-overlay" onClick={() => setImageViewer({ open: false, src: '', title: '' })}>
          <div className="image-viewer-content" onClick={(e) => e.stopPropagation()}>
            <div className="image-viewer-header">
              <h3>{imageViewer.title}</h3>
              <button 
                className="image-viewer-close"
                onClick={() => setImageViewer({ open: false, src: '', title: '' })}
              >
                <X size={24} />
              </button>
            </div>
            <div className="image-viewer-body">
              <img src={imageViewer.src} alt={imageViewer.title} />
            </div>
          </div>
        </div>
      )}

      {/* Edit Engineer Modal */}
      {editModal.open && (
        <div className="image-viewer-overlay" onClick={() => setEditModal({ open: false, engineer: null })}>
          <div className="edit-engineer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3><Edit size={20} /> Edit Engineer</h3>
              <button 
                className="image-viewer-close"
                onClick={() => setEditModal({ open: false, engineer: null })}
              >
                <X size={24} />
              </button>
            </div>
            <div className="edit-modal-body">
              <p className="edit-engineer-name">{editModal.engineer?.fullName} - {editModal.engineer?.businessName}</p>
              
              <div className="edit-form-row">
                <div className="edit-form-field">
                  <label><Briefcase size={14} /> Business Name</label>
                  <Input
                    value={editForm.businessName}
                    onChange={(e) => setEditForm({ ...editForm, businessName: e.target.value })}
                  />
                </div>
                <div className="edit-form-field">
                  <label><Clock size={14} /> Years Trading</label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.yearsTrading}
                    onChange={(e) => setEditForm({ ...editForm, yearsTrading: e.target.value })}
                  />
                </div>
              </div>

              <div className="edit-form-row">
                <div className="edit-form-field">
                  <label><Phone size={14} /> Phone</label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
                <div className="edit-form-field">
                  <label><Mail size={14} /> Email</label>
                  <Input
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="edit-form-field">
                <label><MapPin size={14} /> Coverage Areas (postcodes)</label>
                <Textarea
                  value={editForm.postcodes}
                  onChange={(e) => setEditForm({ ...editForm, postcodes: e.target.value })}
                  placeholder="RM, IG, E, CM (comma or space separated)"
                  rows={2}
                />
                <span className="field-hint">Tip: Use area codes like RM, IG, E instead of RM1, RM2, RM3 etc.</span>
              </div>
              
              <div className="edit-form-field">
                <label><MapPin size={14} /> Business Postcode</label>
                <Input
                  value={editForm.businessPostcode}
                  onChange={(e) => setEditForm({ ...editForm, businessPostcode: e.target.value })}
                  placeholder="RM1 3AD"
                />
              </div>

              <div className="edit-form-field">
                <label><User size={14} /> Bio / About</label>
                <Textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  placeholder="Short description about the engineer and their business..."
                  rows={3}
                  maxLength={500}
                />
                <span className="field-hint">{(editForm.bio || '').length}/500 characters</span>
              </div>

              <div className="edit-form-field">
                <label><Award size={14} /> Accreditations</label>
                <Input
                  value={editForm.accreditations}
                  onChange={(e) => setEditForm({ ...editForm, accreditations: e.target.value })}
                  placeholder="Worcester Accredited, Vaillant Advance, OFTEC (comma separated)"
                />
              </div>
            </div>
            <div className="edit-modal-footer">
              <Button variant="outline" onClick={() => setEditModal({ open: false, engineer: null })}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal.open && (
        <div className="image-viewer-overlay" onClick={() => setResetPasswordModal({ open: false, engineer: null })}>
          <div className="edit-engineer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3><Key size={20} /> Reset Engineer Password</h3>
              <button 
                className="image-viewer-close"
                onClick={() => setResetPasswordModal({ open: false, engineer: null })}
              >
                <X size={24} />
              </button>
            </div>
            <div className="edit-modal-body">
              <p className="edit-engineer-name">{resetPasswordModal.engineer?.fullName} - {resetPasswordModal.engineer?.email}</p>
              
              <div className="edit-form-field">
                <label><Lock size={14} /> Your Admin Password</label>
                <Input
                  type="password"
                  value={resetPasswordForm.adminPassword}
                  onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, adminPassword: e.target.value })}
                  placeholder="Enter your admin password"
                />
              </div>
              
              <div className="edit-form-field">
                <label><Key size={14} /> New Password for Engineer</label>
                <Input
                  type="password"
                  value={resetPasswordForm.newPassword}
                  onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
                  placeholder="Minimum 8 characters"
                />
              </div>
            </div>
            <div className="edit-modal-footer">
              <Button variant="outline" onClick={() => setResetPasswordModal({ open: false, engineer: null })}>
                Cancel
              </Button>
              <Button onClick={handleAdminResetPassword} disabled={saving}>
                {saving ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Start Trial Modal */}
      {trialModal.open && (
        <div className="image-viewer-overlay" onClick={() => setTrialModal({ open: false, engineer: null })}>
          <div className="edit-engineer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3><Clock size={20} /> Start Free Trial</h3>
              <button 
                className="image-viewer-close"
                onClick={() => setTrialModal({ open: false, engineer: null })}
              >
                <X size={24} />
              </button>
            </div>
            <div className="edit-modal-body">
              <p className="edit-engineer-name">{trialModal.engineer?.fullName} - {trialModal.engineer?.businessName}</p>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
                This will make the engineer live on the directory for the selected period. No payment required.
              </p>
              
              <div className="edit-form-field">
                <label><Clock size={14} /> Trial Duration</label>
                <div className="trial-duration-options">
                  {[7, 14, 30, 60].map(days => (
                    <button
                      key={days}
                      className={`trial-duration-btn ${trialDays === days ? 'selected' : ''}`}
                      onClick={() => setTrialDays(days)}
                    >
                      {days} days
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: '12px' }}>
                  <label style={{ fontSize: '13px', color: '#64748b' }}>Or set custom days:</label>
                  <Input
                    type="number"
                    min="1"
                    max="90"
                    value={trialDays}
                    onChange={(e) => setTrialDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 1)))}
                    style={{ width: '120px', marginTop: '4px' }}
                  />
                </div>
              </div>
              
              <div className="trial-summary" data-testid="trial-summary">
                <p>Trial will end on: <strong>{new Date(Date.now() + trialDays * 86400000).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></p>
                <p style={{ fontSize: '13px', color: '#64748b' }}>After the trial, the engineer will be automatically removed from search results until they subscribe.</p>
              </div>
            </div>
            <div className="edit-modal-footer">
              <Button variant="outline" onClick={() => { setTrialModal({ open: false, engineer: null }); setTrialDays(30); }}>
                Cancel
              </Button>
              <Button onClick={handleStartTrial} disabled={saving} className="btn-approve" data-testid="confirm-trial-btn">
                {saving ? 'Starting...' : `Start ${trialDays}-Day Trial`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Compose Email Modal */}
      {emailModal && (
        <div className="image-viewer-overlay" onClick={() => !sendingEmail && setEmailModal(false)}>
          <div className="edit-engineer-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: '600px'}}>
            <div className="edit-modal-header">
              <h3><Megaphone size={20} /> Compose Email to Engineers</h3>
              <button 
                className="image-viewer-close"
                onClick={() => !sendingEmail && setEmailModal(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className="edit-modal-body">
              <div className="edit-form-field">
                <label><Users size={14} /> Send To</label>
                <select
                  value={emailForm.recipients}
                  onChange={(e) => setEmailForm({ ...emailForm, recipients: e.target.value })}
                  className="email-recipient-select"
                  data-testid="email-recipients-select"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '6px',
                    border: '1px solid #e2e8f0', fontSize: '14px', background: 'white'
                  }}
                >
                  <optgroup label="Groups">
                    <option value="all">All Engineers ({engineers.filter(e => ['approved','active','trial_expired'].includes(e.status)).length})</option>
                    <option value="active">Active Engineers ({stats.active})</option>
                    <option value="approved">Approved - Awaiting Payment ({stats.approved})</option>
                    <option value="trial_expired">Trial Expired ({stats.trial_expired})</option>
                  </optgroup>
                  <optgroup label="Individual Engineers">
                    {engineers
                      .filter(e => ['approved','active','trial_expired'].includes(e.status))
                      .sort((a, b) => a.fullName.localeCompare(b.fullName))
                      .map(eng => (
                        <option key={eng.id} value={eng.id}>
                          {eng.fullName} — {eng.businessName || eng.email} ({eng.status})
                        </option>
                      ))
                    }
                  </optgroup>
                </select>
              </div>
              
              <div className="edit-form-field">
                <label><Briefcase size={14} /> Template</label>
                <select
                  value={emailTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  data-testid="email-template-select"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '6px',
                    border: '1px solid #e2e8f0', fontSize: '14px', background: 'white'
                  }}
                >
                  {Object.entries(EMAIL_TEMPLATES).map(([key, tmpl]) => (
                    <option key={key} value={key}>{tmpl.label}</option>
                  ))}
                </select>
                {emailTemplate !== 'custom' && (
                  <span className="field-hint">Template loaded — feel free to edit the subject and message below.</span>
                )}
              </div>

              {cannedResponses.length > 0 && (
                <div className="edit-form-field">
                  <label><FileText size={14} /> Canned response (optional)</label>
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) applyCannedToEmailModal(e.target.value); }}
                    data-testid="email-canned-picker"
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '6px',
                      border: '1px solid #e2e8f0', fontSize: '14px', background: 'white'
                    }}
                  >
                    <option value="">— Pick a canned response —</option>
                    {cannedResponses.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                  <span className="field-hint">Loads the subject + body from your canned library.</span>
                </div>
              )}

              <div className="edit-form-field">
                <label><Mail size={14} /> Subject</label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="Email subject line..."
                  data-testid="email-subject-input"
                />
              </div>
              
              <div className="edit-form-field">
                <label><MessageSquare size={14} /> Message</label>
                <Textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  placeholder="Write your message here..."
                  rows={8}
                  data-testid="email-message-input"
                  style={{minHeight: '180px'}}
                />
                <span className="field-hint">This will be sent as a branded email from support@thegasguide.co.uk. You'll receive a copy.</span>
              </div>
            </div>
            <div className="edit-modal-footer">
              <Button variant="outline" onClick={() => setEmailModal(false)} disabled={sendingEmail}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendCustomEmail} 
                disabled={sendingEmail || !emailForm.subject.trim() || !emailForm.message.trim()}
                data-testid="send-email-submit-btn"
              >
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Canned Response Editor Modal */}
      {cannedEditor.open && (
        <div className="image-viewer-overlay" onClick={() => !savingCanned && setCannedEditor({ ...cannedEditor, open: false })}>
          <div className="edit-engineer-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: '640px'}} data-testid="canned-editor-modal">
            <div className="edit-modal-header">
              <h3><FileText size={20} /> {cannedEditor.mode === 'edit' ? 'Edit canned response' : 'New canned response'}</h3>
              <button className="image-viewer-close" onClick={() => !savingCanned && setCannedEditor({ ...cannedEditor, open: false })}>
                <X size={24} />
              </button>
            </div>
            <div className="edit-modal-body">
              <div className="edit-form-field">
                <label>Name (internal — helps you pick it later)</label>
                <Input
                  value={cannedEditor.name}
                  onChange={(e) => setCannedEditor({ ...cannedEditor, name: e.target.value })}
                  placeholder="e.g. Welcome reply"
                  data-testid="canned-name-input"
                />
              </div>
              <div className="edit-form-field">
                <label>Subject</label>
                <Input
                  value={cannedEditor.subject}
                  onChange={(e) => setCannedEditor({ ...cannedEditor, subject: e.target.value })}
                  placeholder="e.g. Welcome to The Gas Guide, {firstName}"
                  data-testid="canned-subject-input"
                />
              </div>
              <div className="edit-form-field">
                <label>Body</label>
                <Textarea
                  value={cannedEditor.body}
                  onChange={(e) => setCannedEditor({ ...cannedEditor, body: e.target.value })}
                  placeholder={"Hi {firstName},\n\nThanks for getting in touch…\n\nBest regards,\nDan"}
                  rows={12}
                  data-testid="canned-body-input"
                  style={{ minHeight: '220px' }}
                />
                <span className="field-hint">Use <code>{'{firstName}'}</code> anywhere in the subject or body — it&apos;ll be swapped in when the reply is sent.</span>
              </div>
            </div>
            <div className="edit-modal-footer">
              <Button variant="outline" onClick={() => setCannedEditor({ ...cannedEditor, open: false })} disabled={savingCanned}>Cancel</Button>
              <Button onClick={handleSaveCanned} disabled={savingCanned} data-testid="canned-save-btn">
                {savingCanned ? 'Saving…' : (cannedEditor.mode === 'edit' ? 'Save changes' : 'Create response')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal (for Enquiries + Partner replies) */}
      {replyModal.open && (
        <div className="image-viewer-overlay" onClick={() => !sendingReply && setReplyModal({ ...replyModal, open: false })}>
          <div className="edit-engineer-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: '640px'}} data-testid="reply-modal">
            <div className="edit-modal-header">
              <h3><Mail size={20} /> Reply to {replyModal.toName || replyModal.toEmail}</h3>
              <button className="image-viewer-close" onClick={() => !sendingReply && setReplyModal({ ...replyModal, open: false })}>
                <X size={24} />
              </button>
            </div>
            <div className="edit-modal-body">
              <div className="edit-form-field">
                <label>To</label>
                <Input value={replyModal.toEmail} disabled />
              </div>

              {cannedResponses.length > 0 && (
                <div className="edit-form-field">
                  <label><FileText size={14} /> Canned response (optional)</label>
                  <select
                    value={replyModal.cannedResponseId}
                    onChange={(e) => applyCannedToReply(e.target.value)}
                    data-testid="reply-canned-picker"
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '6px',
                      border: '1px solid #e2e8f0', fontSize: '14px', background: 'white'
                    }}
                  >
                    <option value="">— Pick a canned response —</option>
                    {cannedResponses.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                  <span className="field-hint">Loads the subject + body. Edit before sending. <code>{'{firstName}'}</code> will be swapped for the recipient&apos;s first name automatically.</span>
                </div>
              )}

              <div className="edit-form-field">
                <label>Subject</label>
                <Input
                  value={replyModal.subject}
                  onChange={(e) => setReplyModal({ ...replyModal, subject: e.target.value })}
                  placeholder={replyModal.context || 'Subject line'}
                  data-testid="reply-subject-input"
                />
              </div>

              <div className="edit-form-field">
                <label>Message</label>
                <Textarea
                  value={replyModal.message}
                  onChange={(e) => setReplyModal({ ...replyModal, message: e.target.value })}
                  placeholder={"Hi {firstName},\n\nThanks for your enquiry…"}
                  rows={10}
                  data-testid="reply-message-input"
                  style={{ minHeight: '200px' }}
                />
                <span className="field-hint">Sent as a branded email from support@thegasguide.co.uk. Admin gets BCC&apos;d.</span>
              </div>
            </div>
            <div className="edit-modal-footer">
              <Button variant="outline" onClick={() => setReplyModal({ ...replyModal, open: false })} disabled={sendingReply}>Cancel</Button>
              <Button onClick={handleSendReply} disabled={sendingReply || !replyModal.subject.trim() || !replyModal.message.trim()} data-testid="reply-send-btn">
                {sendingReply ? 'Sending…' : 'Send Reply'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
